import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  VStack,
  Text,
  Container,
  SimpleGrid,
  Icon,
  Flex,
  Avatar,
  Badge,
  Image,
  HStack
} from '@chakra-ui/react';
import { FiUser, FiFileText, FiTruck, FiCheckSquare, FiStar, FiClipboard } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { signOutUser } from '../utils/firebaseUtils';
import CustomDropdown from './subComponents/DropDown'
import type { DropdownOption } from './subComponents/DropDown';
import { NotificationBell } from './notifications';

interface NavigationButtonProps {
  icon: React.ComponentType;
  title: string;
  description: string;
  onClick: () => void;
  colorScheme?: string;
  gradient?: string;
  isPopular?: boolean;
}

const NavigationButton: React.FC<NavigationButtonProps> = ({
  icon,
  title,
  description,
  onClick,
  colorScheme = 'blue',
  gradient,
  isPopular = false
}) => {
  const bgGradient = gradient || `linear(135deg, ${colorScheme}.400, ${colorScheme}.600)`;
  const hoverBgGradient = `linear(135deg, ${colorScheme}.500, ${colorScheme}.700)`;
  
  return (
    <Box position="relative" w="100%">
      {isPopular && (
        <Box
          position="absolute"
          top="-8px"
          right="-8px"
          zIndex={2}
          bg="yellow.400"
          color="gray.800"
          borderRadius="full"
          p={1}
          boxShadow="md"
        >
          <Icon as={FiStar} boxSize={4} />
        </Box>
      )}
      <Button
        onClick={onClick}
        h="auto"
        minH={{ base: "160px", md: "180px" }}
        p={{ base: 4, md: 6 }}
        bgGradient={bgGradient}
        color="white"
        variant="solid"
        _hover={{
          bgGradient: hoverBgGradient,
          transform: 'translateY(-4px)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
        }}
        _active={{
          transform: 'translateY(-2px)',
          boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
        }}
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        borderRadius="2xl"
        whiteSpace="normal"
        sx={{
          wordWrap: 'break-word'
        }}
        w="100%"
        maxW="100%"
        boxShadow="0 8px 32px rgba(0,0,0,0.12)"
        border="1px solid"
        borderColor="whiteAlpha.200"
        backdropFilter="blur(10px)"
      >
        <VStack spacing={{ base: 3, md: 4 }} w="full" align="center">
          <Box
            p={3}
            borderRadius="full"
            bg="whiteAlpha.200"
            backdropFilter="blur(10px)"
            border="1px solid"
            borderColor="whiteAlpha.300"
          >
            <Icon as={icon} boxSize={{ base: 6, md: 8 }} color="white" />
          </Box>
          <VStack spacing={2} w="full">
            <Text 
              fontSize={{ base: "lg", md: "xl" }} 
              fontWeight="800"
              textAlign="center"
              lineHeight="shorter"
              noOfLines={2}
              textShadow="0 2px 4px rgba(0,0,0,0.1)"
            >
              {title}
            </Text>
            <Text 
              fontSize={{ base: "sm", md: "md" }} 
              color="whiteAlpha.900"
              textAlign="center"
              lineHeight="short"
              noOfLines={3}
              px={2}
              opacity={0.95}
            >
              {description}
            </Text>
          </VStack>
        </VStack>
      </Button>
    </Box>
  );
};


const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, userProfile, userCompanies, subscriptions, hasFeature, loading, selectedCompany, setSelectedCompanyId, isSuperAdmin } = useAuth();
  const [appVersion, setAppVersion] = useState<string | null>(null);
  
  useEffect(() => {
    const timestamp = Date.now();
    fetch(`/version.json?t=${timestamp}`, {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    })
      .then(res => res.json())
      .then(data => setAppVersion(data.version))
      .catch(() => setAppVersion(null));
  }, []);

  // Group companies by unique name and aggregate roles
  const companyMap = new Map<string, { ids: string[], logo?: string, roles: Set<string> }>();
  userCompanies.forEach(company => {
    const name = company.companyName ?? '';
    if (!companyMap.has(name)) {
      companyMap.set(name, { ids: [company.companyId], logo: company.companyLogo, roles: new Set(company.role ? [company.role] : []) });
    } else {
      const entry = companyMap.get(name)!;
      entry.ids.push(company.companyId);
      if (company.companyLogo && !entry.logo) entry.logo = company.companyLogo;
      if (company.role) entry.roles.add(company.role);
    }
  });

  const companyOptions: DropdownOption[] = Array.from(companyMap.entries()).map(([name, { ids, logo, roles }]) => ({
    id: ids[0], // Use the first companyId for selection
    label: name,
    iconSrc: logo || 'placeholder', // Always provide an iconSrc to show the placeholder when no logo
    subtitle: roles.size > 0 ? `Roles: ${Array.from(roles).join('/').replace(/\//g, '/')} ` : undefined
  }));

  const handleCompanyChange = (companyId: string) => {
    console.log('handleCompanyChange', companyId);
    setSelectedCompanyId(companyId);
  };

  const handleSignOut = async () => {
    try {
      await signOutUser();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  const displayName = userProfile?.name || user?.displayName || user?.email?.split('@')[0] || 'User';

  // If user is not onboarded (no company), show only profile card
  if (userCompanies.length === 0) {
    return (
      <Box minH="100vh" bgGradient="linear(135deg, blue.50, purple.50, pink.50)" w="100%" overflowX="hidden">
        <Box 
          bg="rgba(255, 255, 255, 0.8)" 
          backdropFilter="blur(20px)" 
          borderBottom="1px" 
          borderColor="whiteAlpha.300" 
          px={4} 
          py={6} 
          w="100%"
          boxShadow="0 4px 32px rgba(0,0,0,0.08)"
        >
          <Container maxW="6xl" w="100%">
            <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} direction={{ base: "column", md: "row" }} gap={4} w="100%">
              {/* Mobile: Top row with Sign Out (left) and Notification Bell (right) */}
              <Flex justify="space-between" align="center" w={{ base: "100%", md: "auto" }} order={{ base: 1, md: 1 }}>
                <Button
                  onClick={handleSignOut}
                  variant="ghost"
                  size={{ base: "md", md: "lg" }}
                  colorScheme="red"
                  flexShrink={0}
                  borderRadius="xl"
                  _hover={{
                    bg: "red.50",
                    transform: "translateY(-1px)"
                  }}
                  transition="all 0.2s"
                >
                  Sign Out
                </Button>
                <Box display={{ base: "block", md: "none" }}>
                  <NotificationBell />
                </Box>
              </Flex>
              
              {/* Desktop: Notification bell on the right */}
              <Box display={{ base: "none", md: "block" }}>
                <NotificationBell />
              </Box>
              
              {/* User info section - centered on desktop, second row on mobile */}
              <Flex align="center" gap={4} flex={1} minW={0} justify="center" order={{ base: 2, md: 2 }} w={{ base: "100%", md: "auto" }}>
                <Box position="relative">
                  <Avatar
                    size={{ base: "md", md: "lg" }}
                    name={displayName}
                    src={user?.photoURL || undefined}
                    bg="blue.500"
                    border="3px solid"
                    borderColor="white"
                    shadow="lg"
                  />
                  <Box
                    position="absolute"
                    bottom="0"
                    right="0"
                    w="4"
                    h="4"
                    bg="green.400"
                    borderRadius="full"
                    border="2px solid white"
                  />
                </Box>
                <Box flex={1} minW={0}>
                  <Text
                    fontSize={{ base: "lg", md: "2xl" }}
                    fontWeight="800"
                    bgGradient="linear(135deg, blue.600, purple.600)"
                    bgClip="text"
                    lineHeight="shorter"
                    wordBreak={{ base: "break-word", md: "normal" }}
                    whiteSpace={{ base: "normal", md: "nowrap" }}
                    textAlign={{ base: "left", md: "center" }}
                  >
                    {getGreeting()}, {displayName}!
                  </Text>
                </Box>
              </Flex>
            </Flex>
          </Container>
        </Box>
        <Container maxW="6xl" py={{ base: 8, md: 12 }} px={{ base: 4, md: 8 }} w="100%">
          <VStack spacing={8} w="100%" align="center">
            <Box
              p={8}
              bg="white"
              borderRadius="2xl"
              boxShadow="0 20px 40px rgba(0,0,0,0.1)"
              border="1px solid"
              borderColor="gray.100"
              backdropFilter="blur(10px)"
              w="100%"
              maxW="md"
            >
              <VStack spacing={6}>
                <Text
                  fontSize="2xl"
                  fontWeight="bold"
                  bgGradient="linear(135deg, blue.600, purple.600)"
                  bgClip="text"
                  textAlign="center"
                >
                  Welcome to SmarTrak!
                </Text>
                <NavigationButton
                  icon={FiUser}
                  title="COMPLETE PROFILE"
                  description="Set up your profile to get started"
                  onClick={() => navigate('/profile')}
                  colorScheme="blue"
                  gradient="linear(135deg, blue.400, blue.600)"
                />
                <Text color="red.500" fontWeight="600" textAlign="center" fontSize="sm">
                  You are not on-boarded yet in a company. Please contact support.
                </Text>
              </VStack>
            </Box>
          </VStack>
        </Container>
      </Box>
    );
  }

  // If company subscription is loading, show only profile card (prevent flash)
  if (loading) {
    return (
      <Box minH="100vh" bgGradient="linear(135deg, blue.50, purple.50, pink.50)" w="100%" overflowX="hidden">
        <Box 
          bg="rgba(255, 255, 255, 0.8)" 
          backdropFilter="blur(20px)" 
          borderBottom="1px" 
          borderColor="whiteAlpha.300" 
          px={4} 
          py={6} 
          w="100%"
          boxShadow="0 4px 32px rgba(0,0,0,0.08)"
        >
          <Container maxW="6xl" w="100%">
            <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} direction={{ base: "column", md: "row" }} gap={4} w="100%">
              <HStack spacing={3}>
                <Button
                  onClick={handleSignOut}
                  variant="ghost"
                  size={{ base: "md", md: "lg" }}
                  colorScheme="red"
                  flexShrink={0}
                  borderRadius="xl"
                  _hover={{
                    bg: "red.50",
                    transform: "translateY(-1px)"
                  }}
                  transition="all 0.2s"
                >
                  Sign Out
                </Button>
                <NotificationBell />
              </HStack>
              <Flex align="center" gap={4} flex={1} minW={0} justify="center">
                <Box position="relative">
                  <Avatar
                    size={{ base: "md", md: "lg" }}
                    name={displayName}
                    src={user?.photoURL || undefined}
                    bg="blue.500"
                    border="3px solid"
                    borderColor="white"
                    shadow="lg"
                  />
                  <Box
                    position="absolute"
                    bottom="0"
                    right="0"
                    w="4"
                    h="4"
                    bg="green.400"
                    borderRadius="full"
                    border="2px solid white"
                  />
                </Box>
                <Box flex={1} minW={0}>
                  <Text
                    fontSize={{ base: "lg", md: "2xl" }}
                    fontWeight="800"
                    bgGradient="linear(135deg, blue.600, purple.600)"
                    bgClip="text"
                    lineHeight="shorter"
                    wordBreak={{ base: "break-word", md: "normal" }}
                    whiteSpace={{ base: "normal", md: "nowrap" }}
                    textAlign={{ base: "left", md: "center" }}
                  >
                    {getGreeting()}, {displayName}!
                  </Text>
                </Box>
              </Flex>
            </Flex>
          </Container>
        </Box>
        <Container maxW="6xl" py={{ base: 8, md: 12 }} px={{ base: 4, md: 8 }} w="100%">
          <VStack spacing={8} w="100%" align="center">
            <Box
              p={8}
              bg="white"
              borderRadius="2xl"
              boxShadow="0 20px 40px rgba(0,0,0,0.1)"
              border="1px solid"
              borderColor="gray.100"
              backdropFilter="blur(10px)"
              w="100%"
              maxW="md"
            >
              <VStack spacing={6}>
                <Text
                  fontSize="xl"
                  fontWeight="bold"
                  bgGradient="linear(135deg, blue.600, purple.600)"
                  bgClip="text"
                  textAlign="center"
                >
                  Loading your dashboard...
                </Text>
                <NavigationButton
                  icon={FiUser}
                  title="PROFILE"
                  description="Update your personal information"
                  onClick={() => navigate('/profile')}
                  colorScheme="blue"
                  gradient="linear(135deg, blue.400, blue.600)"
                />
              </VStack>
            </Box>
          </VStack>
        </Container>
      </Box>
    );
  }

  // If company subscription is inactive, show only profile card and message
  if ((subscriptions && subscriptions.length === 0 && !isSuperAdmin)) {
    return (
      <Box minH="100vh" bgGradient="linear(135deg, blue.50, purple.50, pink.50)" w="100%" overflowX="hidden">
        <Box 
          bg="rgba(255, 255, 255, 0.8)" 
          backdropFilter="blur(20px)" 
          borderBottom="1px" 
          borderColor="whiteAlpha.300" 
          px={4} 
          py={6} 
          w="100%"
          boxShadow="0 4px 32px rgba(0,0,0,0.08)"
        >
          <Container maxW="6xl" w="100%">
            <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} direction={{ base: "column", md: "row" }} gap={4} w="100%">
              <Button
                onClick={handleSignOut}
                variant="ghost"
                size={{ base: "md", md: "lg" }}
                colorScheme="red"
                flexShrink={0}
                borderRadius="xl"
                _hover={{
                  bg: "red.50",
                  transform: "translateY(-1px)"
                }}
                transition="all 0.2s"
              >
                Sign Out
              </Button>
              <Flex align="center" gap={4} flex={1} minW={0} justify="center">
                <Box position="relative">
                  <Avatar
                    size={{ base: "md", md: "lg" }}
                    name={displayName}
                    src={user?.photoURL || undefined}
                    bg="blue.500"
                    border="3px solid"
                    borderColor="white"
                    shadow="lg"
                  />
                  <Box
                    position="absolute"
                    bottom="0"
                    right="0"
                    w="4"
                    h="4"
                    bg="orange.400"
                    borderRadius="full"
                    border="2px solid white"
                  />
                </Box>
                <Box flex={1} minW={0}>
                  <Text
                    fontSize={{ base: "lg", md: "2xl" }}
                    fontWeight="800"
                    bgGradient="linear(135deg, blue.600, purple.600)"
                    bgClip="text"
                    lineHeight="shorter"
                    wordBreak={{ base: "break-word", md: "normal" }}
                    whiteSpace={{ base: "normal", md: "nowrap" }}
                    textAlign={{ base: "left", md: "center" }}
                  >
                    {getGreeting()}, {displayName}!
                  </Text>
                </Box>
              </Flex>
            </Flex>
          </Container>
        </Box>
        <Container maxW="6xl" py={{ base: 8, md: 12 }} px={{ base: 4, md: 8 }} w="100%">
          <VStack spacing={8} w="100%" align="center">
            <Box
              p={8}
              bg="white"
              borderRadius="2xl"
              boxShadow="0 20px 40px rgba(0,0,0,0.1)"
              border="1px solid"
              borderColor="gray.100"
              backdropFilter="blur(10px)"
              w="100%"
              maxW="md"
            >
              <VStack spacing={6}>
                <Text
                  fontSize="xl"
                  fontWeight="bold"
                  bgGradient="linear(135deg, orange.500, red.500)"
                  bgClip="text"
                  textAlign="center"
                >
                  Subscription Required
                </Text>
                <NavigationButton
                  icon={FiUser}
                  title="PROFILE"
                  description="Update your personal information"
                  onClick={() => navigate('/profile')}
                  colorScheme="blue"
                  gradient="linear(135deg, blue.400, blue.600)"
                />
                <Text color="red.500" fontWeight="600" textAlign="center" fontSize="sm">
                  Your company subscription has ended. Please renew to use the system again
                </Text>
              </VStack>
            </Box>
          </VStack>
        </Container>
      </Box>
    );
  }

  // ...existing code for onboarded users...
  return (
    <Box minH="100vh" bgGradient="linear(135deg, blue.50, purple.50, pink.50)" w="100%" overflowX="hidden">
      {/* Header */}
      <Box 
        bg="rgba(255, 255, 255, 0.8)" 
        backdropFilter="blur(20px)" 
        borderBottom="1px" 
        borderColor="whiteAlpha.300" 
        px={4} 
        py={6} 
        w="100%"
        boxShadow="0 4px 32px rgba(0,0,0,0.08)"
      >
        <Container maxW="6xl" w="100%">
          <Flex
            direction={{ base: "column", md: "row" }}
            align={{ base: "flex-start", md: "center" }}
            w="100%"
            gap={4}
          >
            {/* Mobile: Top row with Sign Out (left) and Notification Bell (right) */}
            {/* Desktop: Left - Sign Out, Right - Notification Bell */}
            <Flex 
              w={{ base: "100%", md: "auto" }} 
              minW={{ md: "180px" }} 
              justify={{ base: "space-between", md: "flex-start" }} 
              align="center"
              order={{ base: 1, md: 1 }}
            >
              <Button
                onClick={handleSignOut}
                variant="ghost"
                size={{ base: "md", md: "lg" }}
                colorScheme="red"
                flexShrink={0}
                borderRadius="xl"
                _hover={{
                  bg: "red.50",
                  transform: "translateY(-1px)"
                }}
                transition="all 0.2s"
              >
                Sign Out
              </Button>
              <Box display={{ base: "block", md: "none" }}>
                <NotificationBell />
              </Box>
            </Flex>
            
            {/* Desktop: Notification bell on the right */}
            <Box display={{ base: "none", md: "block" }} order={{ base: 3, md: 3 }}>
              <NotificationBell />
            </Box>

            {/* Center: Greeting - Constrained flex */}
            <Box 
              flex={{ base: "1", md: "0 1 auto" }} 
              minW={0} 
              maxW={{ base: "100%", md: "450px" }} 
              display="flex" 
              justifyContent="center" 
              alignItems="center"
              order={{ base: 2, md: 2 }}
            >
              <Flex align="center" gap={4} w="100%" justify="center">
                <Box position="relative">
                  {selectedCompany?.companyLogo ? (
                    <Image
                      src={selectedCompany.companyLogo}
                      alt={`${selectedCompany.companyName} Logo`}
                      w={{ base: "48px", md: "64px" }}
                      h={{ base: "48px", md: "64px" }}
                      objectFit="contain"
                      borderRadius="lg"
                      border="3px solid white"
                      shadow="lg"
                      bg="white"
                      fallback={
                        <Avatar
                          size={{ base: "md", md: "lg" }}
                          name={displayName}
                          src={user?.photoURL || undefined}
                          bg="blue.500"
                          border="3px solid white"
                          shadow="lg"
                        />
                      }
                    />
                  ) : (
                    <Avatar
                      size={{ base: "md", md: "lg" }}
                      name={displayName}
                      src={user?.photoURL || undefined}
                      bg="blue.500"
                      border="3px solid white"
                      shadow="lg"
                    />
                  )}
                  <Box
                    position="absolute"
                    bottom="0"
                    right="0"
                    w="4"
                    h="4"
                    bg="green.400"
                    borderRadius="full"
                    border="2px solid white"
                  />
                </Box>
                <Box minW={0}>
                  <Text
                    fontSize={{ base: "lg", md: "xl" }}
                    fontWeight="800"
                    bgGradient="linear(135deg, blue.600, purple.600)"
                    bgClip="text"
                    lineHeight="shorter"
                    wordBreak={{ base: "break-word", md: "normal" }}
                    whiteSpace={{ base: "normal", md: "nowrap" }}
                    textAlign={{ base: "left", md: "center" }}
                    noOfLines={1}
                  >
                    {getGreeting()}, {displayName}!
                  </Text>
                </Box>
              </Flex>
            </Box>

            {/* Right: Company Dropdown - Expanded width */}
            <Box w={{ base: "100%", md: "auto" }} minW={{ md: "280px" }} display="flex" justifyContent={{ base: "flex-start", md: "flex-end" }} alignItems="center" order={{ base: 3, md: 4 }}>
              {userCompanies.length > 1 ? (
                <CustomDropdown
                  options={companyOptions}
                  selectedId={selectedCompany?.companyId ?? ''}
                  onSelectionChange={handleCompanyChange}
                  placeholder="Select Company"
                  showIcons={true}
                  showBadgeForSingle={true}
                  maxWidth={{ base: "100%", md: "280px" }}
                />
              ) : (
                selectedCompany?.companyName && (
                  <Badge
                    colorScheme="blue"
                    fontSize={{ base: "sm", md: "md" }}
                    px={4}
                    py={2}
                    borderRadius="full"
                    variant="subtle"
                    bg="blue.100"
                    color="blue.800"
                    fontWeight="600"
                  >
                    {selectedCompany.companyName}
                  </Badge>
                )
              )}
            </Box>
          </Flex>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxW="6xl" py={{ base: 8, md: 12 }} px={{ base: 4, md: 8 }} w="100%">
        <VStack spacing={{ base: 6, md: 8 }} w="100%">
          {/* Welcome Message */}
          <Box
            w="100%"
            p={6}
            bg="rgba(255, 255, 255, 0.7)"
            backdropFilter="blur(20px)"
            borderRadius="2xl"
            border="1px solid"
            borderColor="whiteAlpha.300"
            textAlign="center"
          >
            <VStack spacing={0}>
              <Text
                fontSize={{ base: "xl", md: "2xl" }}
                fontWeight="bold"
                bgGradient="linear(135deg, blue.600, purple.600)"
                bgClip="text"
              >
                Welcome to
              </Text>
              <Text
                fontSize={{ base: "xl", md: "2xl" }}
                fontWeight="bold"
                bgGradient="linear(135deg, blue.600, purple.600)"
                bgClip="text"
              >
                SmarTrak Mobile PH
              </Text>
            </VStack>

            <Text
              fontSize={{ base: "sm", md: "md" }}
              color="gray.600"
              opacity={0.8}
            >
              Everything you need to manage your logistics and accounting
            </Text>
          </Box>

          {/* Feature Cards */}
          <SimpleGrid
            columns={{ base: 1, sm: 2, lg: 3 }}
            spacing={{ base: 6, md: 8 }}
            w="100%"
            maxW="6xl"
          >
            <NavigationButton
              icon={FiUser}
              title="PROFILE"
              description="Update your personal information and account settings"
              onClick={() => navigate('/profile')}
              colorScheme="blue"
              gradient="linear(135deg, blue.400, blue.600)"
            />

            {/* Conditionally show feature cards based on subscription */}
            {(hasFeature('reports') || isSuperAdmin) && (
              <NavigationButton
                icon={FiFileText}
                title="REPORTS"
                description="View and filter your reports with date ranges"
                onClick={() => navigate('/reports')}
                colorScheme="green"
                gradient="linear(135deg, green.400, green.600)"
              />
            )}
            {(hasFeature('delivery_monitoring') || isSuperAdmin) && (
              <NavigationButton
                icon={FiTruck}
                title="DELIVERY MONITORING"
                description="Track and monitor delivery status and routes"
                onClick={() => navigate('/delivery-monitoring')}
                colorScheme="orange"
                gradient="linear(135deg, orange.400, red.500)"
                isPopular={true}
              />
            )}
            {(hasFeature('asset_monitoring') || isSuperAdmin) && (
              <NavigationButton
                icon={FiFileText}
                title="ASSET MONITORING"
                description="Manage and track company assets"
                onClick={() => navigate('/asset-management')}
                colorScheme="purple"
                gradient="linear(135deg, purple.400, purple.600)"
              />
            )}
            {(hasFeature("pre_departure_checklist") || isSuperAdmin) && (
              <NavigationButton
                icon={FiCheckSquare}
                title="PRE-DEPARTURE CHECKLIST"
                description="Complete safety and vehicle inspection before departure"
                onClick={() => navigate('/pre-departure-checklist')}
                colorScheme="teal"
                gradient="linear(135deg, teal.400, teal.600)"
                isPopular={true}
              />
            )}
            {(hasFeature("container_van_checklist") || isSuperAdmin) && (
              <NavigationButton
                icon={FiClipboard}
                title="CONTAINER VAN CHECKLIST"
                description="Container van monitoring and safety inspection"
                onClick={() => navigate('/container-van-checklist')}
                colorScheme="cyan"
                gradient="linear(135deg, cyan.400, teal.600)"
                isPopular={false}
              />
            )}
          </SimpleGrid>
        </VStack>
      </Container>
    {/* App version at the bottom */}
    <Box as="footer" w="100%" py={4} mt={8}>
      <Container maxW="6xl">
        <Box
          textAlign="center"
          py={3}
          px={4}
          bg="rgba(255, 255, 255, 0.6)"
          backdropFilter="blur(20px)"
          borderRadius="xl"
          border="1px solid"
          borderColor="whiteAlpha.300"
        >
          <Text 
            fontSize="xs" 
            fontWeight="600"
            bgGradient="linear(135deg, blue.600, purple.600)"
            bgClip="text"
          >
            Mobile SmarTrak PH@{appVersion ? `v${appVersion}` : ''}
          </Text>
          <Text fontSize="xs" color="gray.500" mt={1}>
            Powering your logistics operations
          </Text>
        </Box>
      </Container>
    </Box>
  </Box>
  );
};

export default Dashboard;
